import React, { useState } from 'react';

import { View, Text } from 'react-native';
import { TextInput, Button, Message, ErrorMessage } from '@valet/ui-components';

import { HandleAuthFormInputChange, HandleAuthFormSubmit } from './AuthTypes';
import { authStyles } from './SignIn';
import { FormattedMessage, useIntl } from 'react-intl';

type ResendCodeProps = {
    handleResendCodeAttempt: (email: string) => void;
    handleRedirectSignUp: () => void;
};

export const ResendCode: React.FC<ResendCodeProps> = ({
    handleResendCodeAttempt,
    handleRedirectSignUp,
}) => {
    const intl = useIntl();
    type UserDetails = {
        email: string;
    };

    const [userDetails, setUserDetails] = useState<UserDetails>({
        email: '',
    });
    const [errorMessage, setErrorMessage] = useState<ErrorMessage | undefined>(undefined);

    const handleEmailInputChange: HandleAuthFormInputChange = (value: string) => {
        setUserDetails({ ...userDetails, email: value });
    };

    const handleResendCodeFormSubmit: HandleAuthFormSubmit = async () => {
        setErrorMessage({ content: '', type: 'info', header: '' });

        const { email } = userDetails;

        if (email === '') {
            enum UserKeys {
                email = 'email',
            }

            interface TmpUserDetails {
                [key: string]: UserKeys[keyof UserKeys];
            }

            const tmpUserDetails: TmpUserDetails = userDetails;
            const userKeys: string[] = Object.keys(tmpUserDetails);
            let emptyField;

            for (let i = 0; i < userKeys.length; i++) {
                if (tmpUserDetails[userKeys[i]] === '') {
                    emptyField = userKeys[i];
                    break;
                }
            }

            switch (emptyField) {
                case 'email':
                    return setErrorMessage({
                        content: intl.formatMessage({
                            id: 'authentication.errorEnterEmail',
                            defaultMessage: 'Please enter your email address',
                        }),
                        type: 'warning',
                        header: intl.formatMessage({
                            id: 'authentication.errorMessageHeader',
                            defaultMessage: 'There was an issue',
                        }),
                    });
                default:
                    return setErrorMessage({
                        content: intl.formatMessage({
                            id: 'authentication.errorFillInAllFields',
                            defaultMessage: 'Please fill in all the fields.',
                        }),
                        type: 'warning',
                        header: intl.formatMessage({
                            id: 'authentication.errorMessageHeader',
                            defaultMessage: 'There was an issue',
                        }),
                    });
            }
        }

        try {
            await handleResendCodeAttempt(email);
        } catch (err) {
            setErrorMessage({
                ...errorMessage,
                content: err.message,
                type: 'negative',
                header: 'Error',
            });
        }
    };

    return (
        <View style={resendCodeStyles.ViewParent} testID="data-resendCodePage">
            <View style={resendCodeStyles.ViewForm} testID="data-resendCodeForm">
                <Text style={resendCodeStyles.TextHeader}>
                    <FormattedMessage
                        id="authentication.resendCodeHeader"
                        defaultMessage="Resend Code"
                    />
                </Text>

                {errorMessage && (
                    <View style={resendCodeStyles.ViewErrorMessage}>
                        <Message errorMessage={errorMessage} />
                    </View>
                )}

                <View style={resendCodeStyles.ViewFormGroup}>
                    <Text>
                        {' '}
                        <FormattedMessage
                            id="authentication.yourEmail"
                            defaultMessage="Your Email"
                        />
                    </Text>

                    <TextInput
                        type="text"
                        testID="data-emailInput"
                        onChange={handleEmailInputChange}
                        ariaLabel={intl.formatMessage({
                            id: 'authentication.yourEmail',
                            defaultMessage: 'Your Email',
                        })}
                        value={userDetails.email}
                    />
                </View>

                <View style={resendCodeStyles.ViewFormGroup}>
                    <Button
                        title={intl.formatMessage({
                            id: 'authentication.sendNewVerificationCodeButton',
                            defaultMessage: 'Send New Verification Code',
                        })}
                        onPress={handleResendCodeFormSubmit}
                        testID="data-submitButton"
                    />
                </View>

                <View style={resendCodeStyles.ViewRedirectionLinks}>
                    <Button
                        title={intl.formatMessage({
                            id: 'authentication.signUpLink',
                            defaultMessage: "Don't have an account? Create one now.",
                        })}
                        appearance="ghost"
                        onPress={handleRedirectSignUp}
                    />
                </View>
            </View>
        </View>
    );
};

const resendCodeStyles = authStyles;
